import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { IPermissionCode, usePermission } from "../permission"
import { IAuth } from "../../store/Auth"

/**
 * Returns: the ONE place that says which action exists in which return status, for which side
 * (buyer / seller) and which permission it needs (canvas Ord_ReturnActions + Ord_ReturnDetail).
 * Codes and rules are the backend's (de-order-service routes/api/return/*.ts, logics/return/*.ts,
 * utils/return-state-transitions.ts). The detail action bar, the list row actions and the bulk
 * toolbar all read it: an action that is not available stays visible, aria-disabled, with the reason.
 */
export type ReturnAction =
  | "detail"
  | "create"
  | "offSystemCreate"
  | "propose"
  | "accept"
  | "reject"
  | "dispute"
  | "cancel"
  | "escalate"
  | "managerOverride"
  | "setResolution"
  | "confirmRefund"
  | "inventoryImpact"
  | "bulkDispose"
  | "schedulePickup"
  | "markShipped"
  | "warehouseReceive"
  | "reportTransit"
  | "resolveTransit"
  | "offSystemVerify"
  // POST /returns/bulk-action (one route, one code for every action)
  | "bulkPropose"
  | "bulkReject"
  | "bulkCancel"
  | "bulkAccept"
  | "bulkConfirmRefund"

export const RETURN_ACTION_PERMISSION: Record<ReturnAction, IPermissionCode[]> = {
  detail: [], // return/get.ts has no permission check (scope = buyer or seller business)
  create: ["RETURN_CREATE"], // return/create.ts
  offSystemCreate: ["RETURN_CREATE"], // return/off-system-claim.ts
  propose: ["RETURN_PROPOSE"], // return/propose.ts
  accept: ["RETURN_ACCEPT"], // return/accept.ts
  reject: ["RETURN_REJECT"], // return/reject.ts
  dispute: ["RETURN_DISPUTE"], // return/dispute.ts
  cancel: ["RETURN_CANCEL"], // return/cancel.ts
  escalate: ["RETURN_ESCALATE"], // return/escalate.ts
  managerOverride: ["RETURN_ESCALATE"], // return/escalate-dispute.ts
  setResolution: ["RETURN_SET_RESOLUTION"], // return/set-resolution.ts, set-item-resolution.ts
  confirmRefund: ["RETURN_CONFIRM_REFUND"], // return/confirm-refund.ts
  inventoryImpact: ["RETURN_SET_INVENTORY_IMPACT"], // return/inventory-impact.ts
  bulkDispose: ["RETURN_SET_INVENTORY_IMPACT"], // damaged-inventory/bulk-dispose.ts
  schedulePickup: ["RETURN_SCHEDULE_PICKUP"], // return/schedule-pickup.ts
  markShipped: ["RETURN_MARK_SHIPPED"], // return/mark-shipped.ts
  warehouseReceive: ["RETURN_WAREHOUSE_RECEIVE"], // return/confirm-warehouse-receipt.ts, mark-arrived.ts
  reportTransit: ["RETURN_REPORT_TRANSIT_EXCEPTION"], // return/transit-exception.ts
  resolveTransit: ["RETURN_RESOLVE_TRANSIT"], // return/resolve-transit-exception.ts
  offSystemVerify: ["RETURN_OFFSYSTEM_VERIFY"], // return/verify-seller.ts
  bulkPropose: ["RETURN_PROPOSE"], // return/bulk-action.ts checks RETURN_PROPOSE for every action
  bulkReject: ["RETURN_PROPOSE"],
  bulkCancel: ["RETURN_PROPOSE"],
  bulkAccept: ["RETURN_PROPOSE"],
  bulkConfirmRefund: ["RETURN_PROPOSE"],
}

/** BE Joi `.max(100)` on bulk-action returnRequestIds and damaged-inventory bulk-dispose ids. */
export const RETURN_BULK_MAX = 100

/** Statuses the backend accepts for each action (logics/return/*.ts). `null` = any status. */
export const RETURN_ACTION_STATUSES: Record<ReturnAction, readonly string[] | null> = {
  detail: null,
  create: null,
  offSystemCreate: null,
  propose: ["REQUESTED", "DISPUTED"], // propose.ts proposableStatuses
  accept: ["PROPOSED"], // accept.ts
  reject: ["REQUESTED", "PROPOSED", "DISPUTED"], // reject.ts rejectableStatuses
  dispute: ["PROPOSED"], // dispute.ts
  cancel: ["REQUESTED", "PROPOSED", "DISPUTED"], // cancel.ts cancellableStatuses
  // escalate.ts: any non-terminal status
  escalate: [
    "REQUESTED", "PROPOSED", "ACCEPTED", "DISPUTED", "AWAITING_PICKUP_SCHEDULE", "AWAITING_SHIPMENT_INFO",
    "IN_TRANSIT", "IN_TRANSIT_EXCEPTION", "AWAITING_WAREHOUSE_RECEIPT", "AWAITING_RESOLUTION",
    "REFUND_PENDING", "PARTIALLY_COMPLETED",
  ],
  managerOverride: ["DISPUTED"], // escalate-dispute.ts
  // set-resolution.ts PRE_ACCEPT_STATES + POST_ACCEPT_OVERRIDE_STATES (managerOverride=true)
  setResolution: ["REQUESTED", "PROPOSED", "DISPUTED", "ACCEPTED", "AWAITING_RESOLUTION"],
  confirmRefund: ["REFUND_PENDING"], // confirm-refund.ts
  // Goods are allocated to stock buckets once the return is accepted (spec §9.5).
  inventoryImpact: ["ACCEPTED", "AWAITING_RESOLUTION", "REFUND_PENDING", "COMPLETED"],
  bulkDispose: null,
  schedulePickup: ["AWAITING_PICKUP_SCHEDULE"], // schedule-pickup.ts
  markShipped: ["AWAITING_SHIPMENT_INFO"], // mark-shipped.ts
  warehouseReceive: ["AWAITING_WAREHOUSE_RECEIPT"], // confirm-warehouse-receipt.ts
  reportTransit: ["IN_TRANSIT"], // transit-exception.ts
  resolveTransit: ["IN_TRANSIT_EXCEPTION"], // resolve-transit-exception.ts
  offSystemVerify: null, // verify-seller.ts gates on the claim's verification status, see BLOCKED
  bulkPropose: ["REQUESTED", "DISPUTED"],
  bulkReject: ["REQUESTED", "PROPOSED", "DISPUTED"],
  bulkCancel: ["REQUESTED", "PROPOSED", "DISPUTED"],
  bulkAccept: ["PROPOSED"],
  bulkConfirmRefund: ["REFUND_PENDING"],
}

/** Which business the backend scopes the action to (`where: { sellerBusinessId | buyerBusinessId: myBusinessId }`). */
type SideRule = "seller" | "buyer" | "either"
const SIDE: Record<ReturnAction, SideRule> = {
  detail: "either",
  create: "buyer",
  offSystemCreate: "buyer",
  propose: "seller",
  accept: "buyer",
  reject: "seller",
  dispute: "buyer",
  cancel: "buyer",
  escalate: "seller",
  managerOverride: "seller",
  setResolution: "seller",
  confirmRefund: "seller",
  inventoryImpact: "seller",
  bulkDispose: "seller",
  schedulePickup: "seller",
  markShipped: "either",
  warehouseReceive: "seller",
  reportTransit: "seller",
  resolveTransit: "seller",
  offSystemVerify: "seller",
  // Bulk rows may belong to either side; the server reports per-row failures.
  bulkPropose: "either",
  bulkReject: "either",
  bulkCancel: "either",
  bulkAccept: "either",
  bulkConfirmRefund: "either",
}

/** Anything an action is evaluated against: a list row (IReturnSummary) or the detail (IReturnDetail). */
export type ReturnActionSubject = {
  status?: string | null
  buyerBusinessId?: string | null
  sellerBusinessId?: string | null
  disputeCount?: number | null
  escalatedAt?: string | null
  isOffSystemBuyerClaim?: boolean | null
  sellerVerificationStatus?: string | null
}

type Side = { isBuyer: boolean; isSeller: boolean }

/** Non-permission, non-status reasons (data). Value = i18n key in `returns`. */
const BLOCKED: Partial<Record<ReturnAction, (s: ReturnActionSubject, ctx: { disputeMax: number }) => string | null>> = {
  dispute: (s, ctx) => ((s.disputeCount ?? 0) >= ctx.disputeMax ? "actions.reason.disputeExhausted" : null),
  escalate: (s) => (s.escalatedAt ? "actions.reason.alreadyEscalated" : null),
  offSystemVerify: (s) =>
    s.isOffSystemBuyerClaim && s.sellerVerificationStatus === "PENDING" ? null : "actions.reason.offSystemNotPending",
}

export function returnSide(subject: ReturnActionSubject | null | undefined, myBusinessId: string | null | undefined): Side {
  // Without a current business (platform staff) the side is not known: let the server decide.
  if (!myBusinessId || !subject) return { isBuyer: true, isSeller: true }
  const isBuyer = subject.buyerBusinessId === myBusinessId
  // The list row has no sellerBusinessId; the list is scoped to buyer OR seller (logics/return/list.ts).
  const isSeller = subject.sellerBusinessId ? subject.sellerBusinessId === myBusinessId : !isBuyer
  return { isBuyer, isSeller }
}

/** Pure status check. */
export function returnActionStatusOk(action: ReturnAction, status: string | null | undefined): boolean {
  const allowed = RETURN_ACTION_STATUSES[action]
  return allowed == null || allowed.includes(status ?? "")
}

function sideOk(action: ReturnAction, side: Side): boolean {
  const rule = SIDE[action]
  if (rule === "either") return side.isBuyer || side.isSeller
  return rule === "seller" ? side.isSeller : side.isBuyer
}

export type ReturnActionState = {
  /** Status and side both fit: the action belongs on this return (row actions render only these). */
  applies: boolean
  /** The user may perform it now. */
  enabled: boolean
  /** Why it is unavailable (permission first, then side, status, data), null when enabled. */
  reason: string | null
  /** @dehub/help reason code for `order.returns-*.reason.*` (PERM, SIDE_SELLER, NOT_IN_STATUS, …), null when enabled. */
  code: ReturnReasonCode | null
}

export type ReturnReasonCode = "PERM" | "SIDE_SELLER" | "SIDE_BUYER" | "NOT_IN_STATUS" | "DISPUTE_LIMIT" | "ESCALATED" | "NOT_PENDING"

/** BLOCKED i18n key → help reason code. */
const BLOCK_CODE: Record<string, ReturnReasonCode> = {
  "actions.reason.disputeExhausted": "DISPUTE_LIMIT",
  "actions.reason.alreadyEscalated": "ESCALATED",
  "actions.reason.offSystemNotPending": "NOT_PENDING",
}

/** `proposeTerms` → `propose-terms`: the @dehub/help id of a matrix action (`order.returns-detail.action.{id}`). */
export const returnHelpId = (action: ReturnAction): string =>
  action === "inventoryImpact" ? "allocate" : action === "warehouseReceive" ? "warehouse-receive" : action.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)

/**
 * `permissionState(action, ret?)` → `{ enabled, reason }`. Without `ret` only the permission is
 * checked (page header buttons, bulk toolbar). `stateOf` adds `applies` for row actions.
 */
/** Backend dispute ceiling (de-order-service logics/return/dispute.ts MAX_DISPUTES). */
export const BE_MAX_DISPUTES = 3

export function useReturnActions(opts?: { disputeMax?: number }) {
  const { t } = useTranslation(["returns", "common"])
  const myBusinessId = useSelector(
    (s: { auth: IAuth }) => s.auth.user?.currentBusiness?.id ?? s.auth.user?.currentBusinessId ?? null,
  )
  // The BE dispute ceiling is fixed (logics/return/dispute.ts MAX_DISPUTES); the tenant
  // disputeCountMax setting is not enforced there yet, so callers cannot raise or lower it.
  void opts
  const disputeMax = BE_MAX_DISPUTES

  const granted: Partial<Record<IPermissionCode, boolean>> = {
    RETURN_CREATE: usePermission("RETURN_CREATE").hasAccess,
    RETURN_PROPOSE: usePermission("RETURN_PROPOSE").hasAccess,
    RETURN_ACCEPT: usePermission("RETURN_ACCEPT").hasAccess,
    RETURN_REJECT: usePermission("RETURN_REJECT").hasAccess,
    RETURN_DISPUTE: usePermission("RETURN_DISPUTE").hasAccess,
    RETURN_CANCEL: usePermission("RETURN_CANCEL").hasAccess,
    RETURN_ESCALATE: usePermission("RETURN_ESCALATE").hasAccess,
    RETURN_SET_RESOLUTION: usePermission("RETURN_SET_RESOLUTION").hasAccess,
    RETURN_CONFIRM_REFUND: usePermission("RETURN_CONFIRM_REFUND").hasAccess,
    RETURN_SET_INVENTORY_IMPACT: usePermission("RETURN_SET_INVENTORY_IMPACT").hasAccess,
    RETURN_SCHEDULE_PICKUP: usePermission("RETURN_SCHEDULE_PICKUP").hasAccess,
    RETURN_MARK_SHIPPED: usePermission("RETURN_MARK_SHIPPED").hasAccess,
    RETURN_WAREHOUSE_RECEIVE: usePermission("RETURN_WAREHOUSE_RECEIVE").hasAccess,
    RETURN_REPORT_TRANSIT_EXCEPTION: usePermission("RETURN_REPORT_TRANSIT_EXCEPTION").hasAccess,
    RETURN_RESOLVE_TRANSIT: usePermission("RETURN_RESOLVE_TRANSIT").hasAccess,
    RETURN_OFFSYSTEM_VERIFY: usePermission("RETURN_OFFSYSTEM_VERIFY").hasAccess,
  }
  const grantedKey = JSON.stringify(granted)

  /** «RETURN_PROPOSE» — the code(s) shown under a button («Шаардах эрх: …»). */
  const required = useCallback((action: ReturnAction) => RETURN_ACTION_PERMISSION[action].join(" / "), [])

  const stateOf = useCallback(
    (action: ReturnAction, subject?: ReturnActionSubject | null): ReturnActionState => {
      const codes = RETURN_ACTION_PERMISSION[action]
      const permitted = codes.length === 0 || codes.some((c) => granted[c])
      if (!subject) {
        return {
          applies: true,
          enabled: permitted,
          reason: permitted ? null : t("common:permission.required", { code: codes.join(" / ") }),
          code: permitted ? null : "PERM",
        }
      }
      const side = returnSide(subject, myBusinessId)
      const sideFits = sideOk(action, side)
      const statusFits = returnActionStatusOk(action, subject.status)
      // Off-system verification exists only on an off-system buyer claim; elsewhere it is not an action at all.
      const applies = sideFits && statusFits && (action !== "offSystemVerify" || !!subject.isOffSystemBuyerClaim)
      if (!permitted) return { applies, enabled: false, reason: t("common:permission.required", { code: codes.join(" / ") }), code: "PERM" }
      if (!sideFits) {
        const seller = SIDE[action] === "seller"
        const key = seller ? "actions.reason.sellerOnly" : "actions.reason.buyerOnly"
        return { applies, enabled: false, reason: t(`returns:${key}`), code: seller ? "SIDE_SELLER" : "SIDE_BUYER" }
      }
      if (!statusFits) return { applies, enabled: false, reason: t("returns:actions.reason.notInStatus"), code: "NOT_IN_STATUS" }
      const blockKey = BLOCKED[action]?.(subject, { disputeMax }) ?? null
      return {
        applies,
        enabled: !blockKey,
        reason: blockKey ? t(`returns:${blockKey}`, { max: disputeMax }) : null,
        code: blockKey ? BLOCK_CODE[blockKey] ?? "NOT_IN_STATUS" : null,
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, grantedKey, myBusinessId, disputeMax],
  )

  const permissionState = useCallback(
    (action: ReturnAction, subject?: ReturnActionSubject | null) => {
      const s = stateOf(action, subject)
      return { enabled: s.enabled, reason: s.reason, code: s.code }
    },
    [stateOf],
  )

  return { stateOf, permissionState, required, myBusinessId }
}
